import { create } from 'zustand';
import {
  setAuthSession,
  clearAuthSession,
  isAuthenticated as isStorageAuthenticated,
} from '@/shared/api/storage';

export type AuthStep = 'login' | 'mfa' | 'success';

/**
 * Token data received from token-issuing API responses.
 */
export interface AuthTokenPayload {
  accessToken: string;
  expiresIn: string | number;
  tokenType: string;
  scope: string[];
}

interface AuthState {
  currentStep: AuthStep;
  username: string;
  isLoading: boolean;
  error: string | null;
  sessionId?: string;
  /**
   * Reactive flag that indicates whether a valid session exists in the
   * storage layer.
   */
  isAuthenticated: boolean;
}

interface AuthActions {
  setStep: (step: AuthStep) => void;
  setUsername: (username: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  loginMFARequired: (username: string, sessionId: string) => void;
  loginCompleted: (payload: AuthTokenPayload) => void;
  mfaFulfilled: (payload: AuthTokenPayload) => void;
  handleBackToLogin: () => void;
}

export interface AuthStore extends AuthState, AuthActions {}

const getInitialState = (): AuthState => ({
  currentStep: 'login',
  username: '',
  isLoading: false,
  error: null,
  sessionId: undefined,
  isAuthenticated: isStorageAuthenticated(),
});

export const useAuthStore = create<AuthStore>()((set) => ({
  ...getInitialState(),

  setStep: (step) => set({ currentStep: step, error: null }),

  setUsername: (username) => set({ username }),

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error, isLoading: false }),

  reset: () => {
    clearAuthSession();
    set(getInitialState());
  },

  loginMFARequired: (username, sessionId) =>
    set({
      username,
      sessionId,
      currentStep: 'mfa',
      isLoading: false,
      error: null,
    }),

  loginCompleted: (payload) => {
    setAuthSession(payload);

    set({
      isAuthenticated: true,
      currentStep: 'success',
      isLoading: false,
      error: null,
    });
  },

  mfaFulfilled: (payload) => {
    setAuthSession(payload);

    set({
      isAuthenticated: true,
      currentStep: 'success',
      isLoading: false,
      error: null,
    });
  },

  handleBackToLogin: () => {
    clearAuthSession();
    set({
      currentStep: 'login',
      username: '',
      isLoading: false,
      error: null,
      sessionId: undefined,
      isAuthenticated: false,
    });
  },
}));
